import {
  buildCoachContext,
  buildHeuristicSuggestions,
  type CoachContextInput,
} from '../../../../../shared/heuristics';

export interface AiSuggestionsResponse {
  suggestions: string[];
  notice?: string;
  ai?: {
    provider?: string;
    model?: string;
  };
}

export const COACH_TIMEOUT_MS = 6000;
let inFlight: { key: string; promise: Promise<AiSuggestionsResponse> } | undefined;

const localSuggestions = (context: CoachContextInput, notice: string): AiSuggestionsResponse => ({
  suggestions: buildHeuristicSuggestions(context.tasks, context.history),
  ai: { provider: 'local' },
  notice,
});

const parseSuggestions = (value: unknown): AiSuggestionsResponse => {
  if (
    !value ||
    typeof value !== 'object' ||
    !('suggestions' in value) ||
    !Array.isArray(value.suggestions)
  ) {
    throw new Error('Invalid coach response');
  }
  const suggestions = value.suggestions;
  if (
    suggestions.length !== 3 ||
    !suggestions.every((item): item is string => typeof item === 'string' && Boolean(item.trim()))
  ) {
    throw new Error('Invalid coach response');
  }
  const metadata = 'ai' in value && value.ai && typeof value.ai === 'object' ? value.ai : undefined;
  const provider =
    metadata && 'provider' in metadata && typeof metadata.provider === 'string'
      ? metadata.provider
      : undefined;
  const model =
    metadata && 'model' in metadata && typeof metadata.model === 'string'
      ? metadata.model
      : undefined;

  return {
    suggestions: suggestions.map((item) => item.trim().slice(0, 400)),
    ai: { provider, model },
  };
};

const requestSuggestions = async (
  apiBaseUrl: string,
  context: CoachContextInput,
): Promise<AiSuggestionsResponse> => {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<AiSuggestionsResponse>((_resolve, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new Error('Coach request timed out'));
    }, COACH_TIMEOUT_MS);
  });

  try {
    return await Promise.race([
      (async () => {
        const response = await fetch(`${apiBaseUrl}/v1/ai/suggestions`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(context),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('AI request failed');
        return parseSuggestions(await response.json());
      })(),
      timeout,
    ]);
  } catch {
    return localSuggestions(
      context,
      controller.signal.aborted
        ? 'Online coach timed out. Guidance was generated on this device.'
        : 'Online coach unavailable. Guidance was generated on this device.',
    );
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
};

export const getAiSuggestions = (
  apiBaseUrl: string,
  payload: CoachContextInput,
): Promise<AiSuggestionsResponse> => {
  const context = buildCoachContext(payload);
  const baseUrl = apiBaseUrl.trim().replace(/\/+$/, '');

  if (!baseUrl)
    return Promise.resolve(
      localSuggestions(context, 'Local guidance: no online coach is configured.'),
    );
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return Promise.resolve(
      localSuggestions(context, 'Offline: guidance was generated on this device.'),
    );
  }

  const key = `${baseUrl}\n${JSON.stringify(context)}`;
  if (inFlight?.key === key) return inFlight.promise;

  const promise = requestSuggestions(baseUrl, context).finally(() => {
    if (inFlight?.promise === promise) inFlight = undefined;
  });
  inFlight = { key, promise };
  return promise;
};
